<template lang="pug">
  .journal-page
    .level
      .level-left
        span
          WtTitle(h3) Journal Entry
          .subtitle(v-if="!journalPageJournalLoading && !_.isEmpty(journalPageJournal)")
            .breadcrumb(aria-label="breadcrumbs")
              ul
                li(v-if="fromRoute === 'Sitewide'")
                  RouterLink(
                    :to="{ name: 'Sitewide'}"
                  ) My Program
                li
                  RouterLink(
                    v-if="/profile/.exec($route.path) || fromRoute === 'Sitewide'"
                    :to="{ name: 'ProfilePage', params: { username: _.get(journalPageJournal, 'member.username', '') } }"
                  ) @{{ _.get(journalPageJournal, "member.username", "") }}
                  RouterLink(
                    v-else
                    :to="{ name: 'MyDashboard'}"
                  ) My Dashboard
                li
                  .has-text-semibold &nbsp;&nbsp;&nbsp;{{ date(journalPageJournal.created, "DATE_FORMAT_SHORT_MONTH").formatted }}
      .level-right

    section.section
      WtMediaObject(
        v-if="!_.isEmpty(journalPageJournal)"
        type="journal"
        :data="journalPageJournal"
        :store-comment-action="storeNewJournalComment"
        :edit-comment-action="editJournalComment"
        :delete-comment-action="deleteJournalComment"
      )
      .empty-state(v-if="_.isEmpty(journalPageJournal) && !journalPageJournalLoading")
        section.section
          p.is-size-4-desktop.has-text-weight-bold This entry is either hidden or does not exist.
      BLoading(:active.sync="journalPageJournalLoading" :is-full-page="false")

</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtMediaObject from "@@/WtMediaObject/WtMediaObject";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "JournalPage",

  components: { WtMediaObject, WtTile, WtTitle, WtButton },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  data() {
    return {
      fromRoute: null
    };
  },

  computed: {
    ...mapGetters("members", [
      "journalPageJournal",
      "journalPageJournalLoading"
    ]),

    journalDate() {
      return this.$route.params.journalDate;
    },

    memberId() {
      return this.$route.name === "ProfileJournalPage"
        ? this._.get(
            this.journalPageJournal,
            "member.id",
            this.$route.params.username
          )
        : this.$auth.user().id;
    }
  },

  mounted() {
    this.trackFeatureView("journal", "journal_entry");
  },

  methods: {
    ...mapActions("members", [
      "storeComment",
      "editComment",
      "deleteComment",
      "fetchJournalPageJournal",
      "fetchJournals"
    ]),
    ...mapActions(["snackBar"]),

    async storeNewJournalComment(payload) {
      Object.assign(payload, {
        memberId: this.$auth.user().id
      });

      const response = await this.storeComment(payload);

      this.fetchJournalPageJournal({
        memberId: this.memberId,
        journalDate: this.journalDate
      });

      this.fetchJournals({
        memberId: payload.profileId,
        setById: true
      });

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 9000,
          message: `There was an error processing your comment.
                    Please try again. Or please reach out to wellness@be-engaged.co.`
        });
      } else {
        this.snackBar({
          message: `Thank you for making a comment!`
        });
      }
    },

    async editJournalComment(payload) {
      const response = await this.editComment(payload);

      this.fetchJournalPageJournal({
        memberId: this.memberId,
        journalDate: this.journalDate
      });

      this.fetchJournals({
        memberId: payload.profileId,
        setById: true
      });

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 9000,
          message: `There was an error processing your comment.
                    Please try again. Or please reach out to wellness@be-engaged.co.`
        });
      } else {
        this.snackBar({
          message: `Your comment has been updated.`
        });
      }
    },

    async deleteJournalComment(payload) {
      this.$buefy.dialog.confirm({
        title: "Delete This Comment",
        confirmText: "Delete This Comment",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want delete this comment?",
        onConfirm: async () => {
          const response = await this.deleteComment(payload);

          this.fetchJournalPageJournal({
            memberId: this.memberId,
            journalDate: this.journalDate
          });

          this.fetchJournals({
            memberId: payload.profileId,
            setById: true
          });

          if (response.error) {
            this.snackBar({
              type: "is-danger",
              duration: 9000,
              message: `There was an error processing your comment.
                      Please try again. Or please reach out to wellness@be-engaged.co.`
            });
          } else {
            this.snackBar({
              message: `Your comment has been deleted.`
            });
          }
        }
      });
    }
  },

  beforeRouteEnter(to, from, next) {
    next(async vm => {
      vm.fromRoute = from.name;

      const memberId =
        to.name === "ProfileJournalPage"
          ? vm.$route.params.username
          : to.name === "JournalPage"
          ? vm.$auth.user().id
          : null;

      vm.fetchJournalPageJournal({
        memberId,
        journalDate: vm.journalDate
      });
    });
  }
};
</script>

<style lang="sass" scoped>
.journal-page
  .section
    min-height: 50vh
</style>
